/**
 * Bundle list — shared init for PDP and Quick Add modal.
 *
 * Markup expected (from snippets/bundle-list-render.liquid):
 *   [data-bundle-list][data-bl-discount-type][data-bl-discount-value]
 *     [data-bundle-list-accordion][data-list-index]
 *       [data-bundle-list-card][data-variant-id][data-variant-price][data-product-title][data-product-handle]
 *
 * Price display targets (in caller-supplied priceScope):
 *   [data-bl-total]    — discounted total
 *   [data-bl-compare]  — original sum (shown only when discount > 0)
 *
 * window.initBundleList({ root, priceScope }) → { recompute, getItems }
 * window.bundleListGetItems(root) → [{ id, quantity }]
 */
(function () {
  function moneyFmt() {
    return window.money_without_trailing_zeros_format ||
      window.money_format ||
      '${{amount}}';
  }

  function fmt(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFmt());
    }
    return String(cents);
  }

  function recomputeFor(root, priceScope) {
    const totalEl = priceScope.querySelector('[data-bl-total]');
    const compareEl = priceScope.querySelector('[data-bl-compare]');
    const discountType = root.dataset.blDiscountType || 'none';
    const discountValue = parseFloat(root.dataset.blDiscountValue) || 0;

    let sum = 0;
    root.querySelectorAll('[data-bundle-list-accordion]').forEach(function (acc) {
      const sel = acc.querySelector('[data-bundle-list-card].is-selected');
      if (!sel) return;
      sum += parseFloat(sel.dataset.variantPrice) || 0;
    });

    let total = sum;
    let showCompare = false;
    if (discountType === 'percentage' && discountValue > 0) {
      total = Math.round((sum * (100 - discountValue)) / 100);
      showCompare = true;
    } else if (discountType === 'fixed' && discountValue > 0) {
      total = Math.max(0, sum - discountValue * 100);
      showCompare = true;
    }

    if (totalEl) totalEl.textContent = fmt(total);
    if (compareEl) {
      if (showCompare && sum > total) {
        compareEl.textContent = fmt(sum);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  function getItemsFromRoot(root) {
    const items = [];
    root.querySelectorAll('[data-bundle-list-accordion]').forEach(function (acc) {
      const sel = acc.querySelector('[data-bundle-list-card].is-selected');
      if (!sel) return;
      const vid = sel.dataset.variantId;
      if (vid) items.push({ id: vid, quantity: 1 });
    });
    return items;
  }

  function attachCardHandlers(root, priceScope) {
    root.querySelectorAll('[data-bundle-list-card]').forEach(function (card) {
      card.addEventListener('click', function () {
        const listIndex = card.dataset.listIndex;
        const acc = root.querySelector(
          '[data-bundle-list-accordion][data-list-index="' + listIndex + '"]'
        );
        if (!acc) return;
        acc.querySelectorAll('[data-bundle-list-card]').forEach(function (c) {
          c.classList.remove('is-selected');
          c.setAttribute('aria-pressed', 'false');
        });
        card.classList.add('is-selected');
        card.setAttribute('aria-pressed', 'true');
        acc.dataset.selectedTitle = card.dataset.productTitle || '';
        acc.dataset.selectedHandle = card.dataset.productHandle || '';
        const label = acc.querySelector('[data-bundle-list-acc-selected]');
        if (label) label.textContent = card.dataset.productTitle || '';
        recomputeFor(root, priceScope);
      });
    });
  }

  /**
   * Simple accordion init for bundle list — used when the standard accordion-manager
   * isn't available (e.g. in the Quick Add modal). Single-open behavior.
   * Uses event delegation so it works regardless of inner-DOM mutations.
   */
  function attachAccordionFallback(root) {
    if (root.dataset.bundleAccordionInited === 'true') return;
    root.dataset.bundleAccordionInited = 'true';

    // Set initial visual state for each accordion item.
    root.querySelectorAll('.bundle-list-accordion').forEach(function (item) {
      const btn = item.querySelector('[data-accordion-toggle]');
      const collapse = item.querySelector('.accordion-collapse');
      if (!btn || !collapse) return;
      const initiallyOpen = btn.getAttribute('aria-expanded') === 'true';
      if (initiallyOpen) {
        collapse.classList.add('show');
        collapse.style.height = 'auto';
        item.classList.add('active');
      } else {
        collapse.style.height = '0';
        collapse.classList.remove('show');
        item.classList.remove('active');
      }
    });

    // Single delegated click handler on the root.
    root.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-accordion-toggle]');
      if (!btn || !root.contains(btn)) return;
      const item = btn.closest('.bundle-list-accordion');
      if (!item) return;
      const collapse = item.querySelector('.accordion-collapse');
      if (!collapse) return;

      e.preventDefault();

      const isOpen = btn.getAttribute('aria-expanded') === 'true';

      // Close any other open accordion in this root.
      root.querySelectorAll('.bundle-list-accordion').forEach(function (other) {
        if (other === item) return;
        const ob = other.querySelector('[data-accordion-toggle]');
        const oc = other.querySelector('.accordion-collapse');
        if (!ob || !oc) return;
        if (ob.getAttribute('aria-expanded') === 'true') {
          ob.setAttribute('aria-expanded', 'false');
          oc.style.height = oc.scrollHeight + 'px';
          void oc.offsetHeight;
          oc.style.height = '0';
          oc.classList.remove('show');
          other.classList.remove('active');
        }
      });

      if (isOpen) {
        btn.setAttribute('aria-expanded', 'false');
        collapse.style.height = collapse.scrollHeight + 'px';
        void collapse.offsetHeight;
        collapse.style.height = '0';
        collapse.classList.remove('show');
        item.classList.remove('active');
      } else {
        btn.setAttribute('aria-expanded', 'true');
        collapse.style.height = collapse.scrollHeight + 'px';
        collapse.classList.add('show');
        item.classList.add('active');
        setTimeout(function () {
          if (btn.getAttribute('aria-expanded') === 'true') {
            collapse.style.height = 'auto';
          }
        }, 320);
      }
    });
  }

  window.bundleListGetItems = getItemsFromRoot;

  window.initBundleList = function (opts) {
    const root = opts && opts.root;
    if (!root) return null;
    if (root.dataset.bundleListInited === 'true') {
      return {
        recompute: function () {
          recomputeFor(root, opts.priceScope || root);
        },
        getItems: function () {
          return getItemsFromRoot(root);
        },
      };
    }
    root.dataset.bundleListInited = 'true';
    const priceScope = opts.priceScope || root.closest('.product-page') || document;
    attachCardHandlers(root, priceScope);
    if (opts.useFallbackAccordion) {
      attachAccordionFallback(root);
    }
    recomputeFor(root, priceScope);
    return {
      recompute: function () {
        recomputeFor(root, priceScope);
      },
      getItems: function () {
        return getItemsFromRoot(root);
      },
    };
  };
})();
